import { ScrollText, ArrowRight, Lock, ExternalLink, ShieldAlert, Scale } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Reveal, Overline } from "./Reveal";
import { useLang } from "../context/LanguageContext";
import { openPdf } from "../utils/pdf";

const UI = {
  sk: {
    overline: "Dokumenty na stiahnutie",
    title: "Začnite správnym krokom",
    text: "Stiahnite si sprievodcu celým procesom. Žiadosť a Kódex nájdete po bezplatnej registrácii vo svojom profile — odtiaľ prebieha celá ďalšia komunikácia.",
    docs: [
      { Icon: ScrollText, title: "Postup certifikácie", desc: "Prehľadný sprievodca celým procesom krok za krokom.", file: "/postup-certifikacie-saubereko.pdf", tag: "Sprievodca" },
    ],
    cta: "Otvoriť",
    regTitle: "Najskôr sa zaregistrujte",
    regText: "Žiadosť o certifikáciu aj Kódex sú dostupné po registrácii priamo vo vašom profile. Po registrácii s vami komunikujeme a vediete celý proces na jednom mieste.",
    regCta: "Registrovať a začať",
    noticeFraud: "Spoločnosť SauberEko® si nikdy nepýta žiadne poplatky vopred. Akýkoľvek poplatok sa uhrádza výhradne až po overení a schválení vašej žiadosti — chráňte sa pred podvodníkmi.",
    noticeRights: "SauberEko® si vyhradzuje výhradné právo na spoluprácu a môže ju odmietnuť alebo ukončiť v prípade poškodenia dobrého mena značky.",
  },
  en: {
    overline: "Documents to download",
    title: "Start the right way",
    text: "Download the guide to the whole process. The application and the Code are available in your profile after a free registration — all further communication runs from there.",
    docs: [
      { Icon: ScrollText, title: "Certification process", desc: "A clear step-by-step guide through the whole process.", file: "/postup-certifikacie-saubereko.pdf", tag: "Guide" },
    ],
    cta: "Open",
    regTitle: "Register first",
    regText: "The certification application and the Code are available in your profile after a free registration — all further communication runs from there.",
    regCta: "Register and start",
    noticeFraud: "SauberEko® never asks for any fees in advance. Any fee is paid solely after your application has been verified and approved — protect yourself from fraudsters.",
    noticeRights: "SauberEko® reserves the exclusive right to cooperation and may refuse or terminate it in the event of damage to the good name of the brand.",
  },
  de: {
    overline: "Dokumente zum Herunterladen",
    title: "Beginnen Sie richtig",
    text: "Laden Sie den Leitfaden zum gesamten Prozess herunter. Antrag und Kodex finden Sie nach kostenloser Registrierung in Ihrem Profil — die gesamte weitere Kommunikation läuft von dort.",
    docs: [
      { Icon: ScrollText, title: "Zertifizierungsablauf", desc: "Ein klarer Schritt-für-Schritt-Leitfaden durch den gesamten Prozess.", file: "/postup-certifikacie-saubereko.pdf", tag: "Leitfaden" },
    ],
    cta: "Öffnen",
    regTitle: "Zuerst registrieren",
    regText: "Der Zertifizierungsantrag und der Kodex sind nach der Registrierung in Ihrem Profil verfügbar. Nach der Registrierung kommunizieren wir mit Ihnen und Sie verwalten den gesamten Prozess an einem Ort.",
    regCta: "Registrieren und starten",
    noticeFraud: "SauberEko® verlangt niemals Gebühren im Voraus. Jegliche Gebühr wird ausschließlich nach Prüfung und Genehmigung Ihres Antrags bezahlt — schützen Sie sich vor Betrügern.",
    noticeRights: "SauberEko® behält sich das ausschließliche Recht auf Zusammenarbeit vor und kann diese im Falle einer Schädigung des guten Namens der Marke verweigern oder beenden.",
  },
};

export const DocumentsDownload = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const ui = UI[lang] || UI.sk;

  return (
    <section
      id="documents"
      data-testid="documents-section"
      className="relative z-10 py-24 md:py-32 bg-[#050505] border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <Overline testId="documents-overline">{ui.overline}</Overline>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight max-w-2xl">
            {ui.title}
          </h2>
          <p className="text-white/55 text-lg leading-relaxed mt-6 max-w-2xl">{ui.text}</p>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6 mt-14 items-stretch">
          {ui.docs.map(({ Icon, title, desc, file, tag }, i) => (
            <Reveal key={i} delay={0.1 * i}>
              <button
                type="button"
                onClick={() => navigate("/postup")}
                data-testid={`document-card-${i}`}
                className="group relative flex flex-col h-full w-full text-left bg-[#0a0a0a] border border-white/10 p-8 hover:border-[#D4AF37]/50 transition-colors duration-300 overflow-hidden"
              >
                <div className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-[#D4AF37]/[0.06] blur-2xl group-hover:bg-[#D4AF37]/[0.12] transition-colors duration-500" />
                <span className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]/70 mb-6">{tag}</span>
                <span className="w-14 h-14 flex items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-black transition-colors duration-300 mb-6">
                  <Icon size={24} strokeWidth={1.5} />
                </span>
                <h3 className="font-display text-2xl leading-snug mb-3">{title}</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1">{desc}</p>
                <span className="inline-flex items-center gap-2 text-[#D4AF37] text-sm font-semibold mt-8 group-hover:gap-3 transition-all duration-300">
                  <ExternalLink size={16} /> {ui.cta}
                </span>
              </button>
            </Reveal>
          ))}

          <Reveal delay={0.15}>
            <div
              data-testid="documents-register-cta"
              className="relative flex flex-col h-full bg-gradient-to-br from-[#1a1407] to-[#0a0a0a] border border-[#D4AF37]/30 p-8 overflow-hidden"
            >
              <div className="absolute -right-10 -bottom-10 w-36 h-36 rounded-full bg-[#D4AF37]/[0.08] blur-3xl" />
              <span className="w-14 h-14 flex items-center justify-center border border-[#D4AF37]/40 text-[#D4AF37] mb-6">
                <Lock size={24} strokeWidth={1.5} />
              </span>
              <h3 className="font-display text-2xl leading-snug mb-3">{ui.regTitle}</h3>
              <p className="text-white/55 text-sm leading-relaxed flex-1">{ui.regText}</p>
              <a
                href="/ziadost"
                data-testid="documents-register-btn"
                className="group inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-semibold px-6 py-3.5 mt-8 hover:bg-[#F2E3C6] transition-colors duration-300"
              >
                {ui.regCta}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="grid sm:grid-cols-2 gap-4 mt-8" data-testid="documents-notices">
            <div className="flex items-start gap-3 border border-[#D4AF37]/20 bg-[#0a0a0a] p-5">
              <ShieldAlert size={20} className="text-[#D4AF37] shrink-0 mt-0.5" />
              <p className="text-white/55 text-sm leading-relaxed">{ui.noticeFraud}</p>
            </div>
            <div className="flex items-start gap-3 border border-[#D4AF37]/20 bg-[#0a0a0a] p-5">
              <Scale size={20} className="text-[#D4AF37] shrink-0 mt-0.5" />
              <p className="text-white/55 text-sm leading-relaxed">{ui.noticeRights}</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
